import React, { forwardRef } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { createUseStyles } from 'react-jss'

import { stylesClasses, generateKey } from '../../utils/classes'
import { responsiveStyles, responsiveDinamicStyles } from '../../utils/styles'

import mediaQueries from '../../constants/mediaQueries'
import theme from '../../constants/theme'

const COLORS = { ...theme['colors'], transparent: 'transparent' }
const SIDES_SPACING_NAMES = ['', 'top', 'right', 'bottom', 'left', 'x', 'y']
const SPACING_VALUES = {
	0: 0,
	1: 0.125,
	2: 0.25,
	3: 0.5,
	4: 0.75,
	5: 1,
	6: 1.25,
	7: 1.5,
	8: 2,
	9: 2.5,
	10: 3,
	11: 4,
	12: 5,
	13: 6.25,
	14: 7.5,
	15: 9
}

/**
 * Return CSS pair {property: value} for each css side
 * @param {string} property - CSS property [example]: "padding"
 * @param {string} side - CSS side of the property [example]: "top"
 * @param {(number|string)} - CSS value
 * @return {Object} [example]: {paddingTop: 5}
 */
function sideStyle(property, side, value) {
	if (SIDES_SPACING_NAMES.includes(side)) {
		if (property === 'padding' || property === 'margin') {
			if (side === 'x') {
				return {
					[`${property}Right`]: value,
					[`${property}Left`]: value
				}
			}
			if (side === 'y') {
				return {
					[`${property}Top`]: value,
					[`${property}Bottom`]: value
				}
			}
		}
		const capitalizeSide = side.charAt(0).toUpperCase() + side.slice(1)
		return { [`${property}${capitalizeSide}`]: value }
	}
}

/**
 * Generates the CSS pair {property: value} for a given mediaQuery prefix,
 * each padding css property, computing the value based on the defined spacers.
 * @param {string} mq - The mediaQuery prefix value
 * @return {Object} styles - The merged CSS pairs
 */
function generatePaddingStyles(mq) {
	const spacingPairs = new Map(Object.entries(SPACING_VALUES))
	let styles = {}

	spacingPairs.forEach((value, key) => {
		const spacingVal = key
		const spacingScalar = value

		SIDES_SPACING_NAMES.forEach((side) => {
			if (spacingScalar !== 'auto') {
				let key
				side === ''
					? (key = generateKey('p--', spacingVal, mq))
					: (key = generateKey('p--' + side + '-', spacingVal, mq))
				const cssVal = `${1 * spacingScalar}rem`
				const cssResult = sideStyle('padding', side, cssVal)
				styles[key] = responsiveStyles(mq, { ...cssResult })
			}
		})
	})

	return styles
}

/**
 * Generates the CSS pair {property: value} for a given mediaQuery prefix,
 * each margin css property, computing the value based on the defined spacers.
 * @param {string} mq - The mediaQuery prefix value
 * @return {Object} styles - The merged CSS pairs
 */
function generateMarginStyles(mq) {
	const spacingPairs = new Map(Object.entries(SPACING_VALUES))
	let styles = {}

	spacingPairs.forEach((value, key) => {
		const spacingVal = key
		const spacingScalar = value

		SIDES_SPACING_NAMES.forEach((side) => {
			if (spacingScalar !== 'auto') {
				let key
				side === ''
					? (key = generateKey('m--', spacingVal, mq))
					: (key = generateKey('m--' + side + '-', spacingVal, mq))
				const cssVal = `${1 * spacingScalar}rem`
				const cssResult = sideStyle('margin', side, cssVal)
				styles[key] = responsiveStyles(mq, { ...cssResult })
			}
		})
	})

	return styles
}

/**
 * Generates the CSS pair {property: value} for a given mediaQuery prefix,
 * each textAlignt css property.
 * @param {string} mq - The mediaQuery prefix value
 * @return {Object} styles - The merged CSS pairs
 */
function generateHorizontalAlignStyles(mq) {
	let styles = {}
	const textAlignmentValues = ['left', 'center', 'right', 'justify']
	textAlignmentValues.forEach((alignment) => {
		const key = generateKey('textAlign--', alignment, mq)
		const cssResult = { textAlign: alignment }
		styles[key] = responsiveStyles(mq, { ...cssResult })
	})
	return styles
}

/**
 * Generates the computed css box-shadow style, depending on the passed parameteres.
 * @param {number} horizontalOff - Horizontal Offset of the shadow, in pixels.
 * @param {number} verticalOff - Vertical Offset of the shadow, in pixels.
 * @param {number} horizontalOff - Horizontal Offset of the shadow, in pixels.
 * @param {number} blur - blur of the shadow, in pixels.
 * @param {number} spread - spread Offset of the shadow, in pixels
 * @param {string} color - Color of the shadow, color-name, hex value or rgba.
 * @return {string | Object} - The string merging all box-shadow options, or the corresponding one for each mediaQuery
 */
function mqBoxShadow(horizontalOff, verticalOff, blur, spread, color) {
	if (
		(!horizontalOff && !(horizontalOff === 0)) ||
		(!verticalOff && !(verticalOff === 0)) ||
		(!color && !(color === 0))
	) {
		return null
	}

	let boxShadowStyles = {}
	let hzOff, vrOff, blr, spr, clr

	Object.keys(mediaQueries).forEach((mq) => {
		hzOff = typeof horizontalOff === 'object' ? horizontalOff[mq] : horizontalOff
		vrOff = typeof verticalOff === 'object' ? verticalOff[mq] : verticalOff
		clr = typeof color === 'object' ? color[mq] : color

		if (typeof blur === 'object') {
			blr = blur[mq] + 'px'
		} else if (typeof blur === 'number') {
			blr = blur + 'px'
		} else {
			blr = ''
		}

		if (blur && typeof spread === 'object') {
			spr = spread[mq] + 'px'
		} else if (blur && typeof spread === 'number') {
			spr = spread + 'px'
		} else {
			spr = ''
		}

		boxShadowStyles[mq] = `${hzOff}px ${vrOff}px ${blr} ${spr} ${clr}`.replace(/\s+/g, ' ').trim()
	})

	return boxShadowStyles
}

/**
 * Return the hex color value correspondent to the given string name.
 * @param {string} colorName - The name of the color, one from the color variables list.
 * @return {string | Object} - The hex value of the color, or the corresponding one for each mediaQuery.
 */
function colorVarConversion(colorName) {
	if (typeof colorName !== 'string' && typeof colorName !== 'object') {
		return null
	}

	const colorPairs = new Map(Object.entries(COLORS))
	if (typeof colorName === 'string') {
		if (colorPairs.has(colorName)) {
			return COLORS[colorName]
		}
	}

	let colorsMq = {}
	if (typeof colorName === 'object') {
		Object.keys(colorName).forEach((mq) => {
			if (colorPairs.has(colorName[mq])) {
				colorsMq[mq] = COLORS[colorName[mq]]
			}
		})
		return colorsMq
	}
	return null
}

/**
 * Generates the computed CSS styles corresopndent to the passed props
 * @param {object} props - The props of the Component.
 * @return {Object} styles - The hex value of the color, or the corresponding one for each mediaQuery.
 */
function generateDynamicStyles(props) {
	const boxShadowStyles = mqBoxShadow(
		props.shadowHoriz,
		props.shadowVert,
		props.shadowBlur,
		props.shadowSpread,
		props.shadowColor
	)

	const styleProps = {
		borderWidth: props.border,
		borderTopWidth: props.border ? null : (props.borderTop ? props.borderTop : 0),
		borderRightWidth: props.border ? null : (props.borderRight ? props.borderRight : 0),
		borderBottomWidth: props.border ? null : (props.borderBottom ? props.borderBottom : 0),
		borderLeftWidth: props.border ? null : (props.borderLeft ? props.borderLeft : 0),
		borderColor: colorVarConversion(props.borderColor),
		borderRadius: props.borderCircle ? '50%' : props.borderRadius,
		borderStyle:
			props.border || props.borderTop || props.borderRight || props.borderBottom || props.borderLeft
				? 'solid'
				: 'none',
		backgroundColor: colorVarConversion(props.bgColor),
		overflow: props.overflow,
		overflowX: props.overflowX,
		overflowY: props.overflowY,
		position: props.position,
		top: props.positionedTop,
		right: props.positionedRight,
		bottom: props.positionedBottom,
		left: props.positionedLeft,
		boxShadow: boxShadowStyles,
		// textAlign: props.hAlign,
		verticalAlign: props.vAlign,
		width: props.width,
		height: props.height
	}

	let styles = {}

	Object.entries(styleProps).forEach(([propKey, propValue]) => {
		if (propValue !== null && typeof propValue !== 'undefined') {
			// if prop is not "undefined" or "null"

			if (typeof propValue === 'number') {
				// if prop is a number or a string
				styles = {
					...styles,
					[propKey]: propValue + 'px'
				}
			} else if (typeof propValue === 'string') {
				styles = {
					...styles,
					[propKey]: propValue
				}
			} else if (typeof propValue === 'object') {
				// if prop is an object {sm: 5, md....}, generate css rule for each one

				Object.keys(propValue).forEach((mq) => {
					if (mediaQueries[mq]) {
						let cssValue = {}
						typeof propValue[mq] === 'number'
							? (cssValue = { [propKey]: `${propValue[mq]}px` })
							: (cssValue = { [propKey]: propValue[mq] })
						const cssResult = responsiveDinamicStyles(mq, { ...cssValue })

						// medaiQueries are objects within the styles object
						// we need to track their key to avoid overwrite them
						if ('mediaQkey' in cssResult) {
							styles[cssResult['mediaQkey']] = {
								...styles[cssResult['mediaQkey']],
								...cssResult['stylesVal']
							}
						} else {
							styles = {
								...styles,
								...cssResult
							}
						}
					}
				})
			}
		}
	})
	return styles
}

/**
 * Generate and merge all styles.
 * Then generate the JSS
 */
const styles = {
	// Dynamic CSS
	styles: (props) => ({
		...generateDynamicStyles(props)
	}),
	// Static CSS
	...Object.keys(mediaQueries).reduce(
		(styles, mq) => ({
			...styles,
			...generatePaddingStyles(mq),
			...generateMarginStyles(mq),
			...generateHorizontalAlignStyles(mq),
		}),
		{}
	)
}

const useStyles = createUseStyles(styles, { name: 'Vb' })

const Box = forwardRef((props, ref) => {
	const {
		component: Component = 'div',
		children,
		className,
		padding,
		paddingX,
		paddingY,
		paddingTop,
		paddingRight,
		paddingBottom,
		paddingLeft,
		margin,
		marginX,
		marginY,
		marginTop,
		marginRight,
		marginBottom,
		marginLeft,
		border,
		borderTop,
		borderRight,
		borderBottom,
		borderLeft,
		borderColor,
		borderCircle,
		borderRadius,
		bgColor,
		overflow,
		overflowX,
		overflowY,
		position,
		positionedTop,
		positionedRight,
		positionedBottom,
		positionedLeft,
		shadowHoriz,
		shadowVert,
		shadowBlur,
		shadowSpread,
		shadowColor,
		vAlign,
		hAlign,
		width,
		height,
		...other
	} = props

	const classes = useStyles(props)

	// If customStyleProps are setted, stylesClasses() matches the key
	// with the unique CSS selector generated by the JSS
	return (
		<Component
			ref={ref}
			className={clsx(
				classes.styles,
				padding && stylesClasses(padding, 'p--', classes),
				paddingX && stylesClasses(paddingX, 'p--x-', classes),
				paddingY && stylesClasses(paddingY, 'p--y-', classes),
				paddingTop && stylesClasses(paddingTop, 'p--top-', classes),
				paddingRight && stylesClasses(paddingRight, 'p--right-', classes),
				paddingBottom && stylesClasses(paddingBottom, 'p--bottom-', classes),
				paddingLeft && stylesClasses(paddingLeft, 'p--left-', classes),
				margin && stylesClasses(margin, 'm--', classes),
				marginX && stylesClasses(marginX, 'm--x-', classes),
				marginY && stylesClasses(marginY, 'm--y-', classes),
				marginTop && stylesClasses(marginTop, 'm--top-', classes),
				marginRight && stylesClasses(marginRight, 'm--right-', classes),
				marginBottom && stylesClasses(marginBottom, 'm--bottom-', classes),
				marginLeft && stylesClasses(marginLeft, 'm--left-', classes),
				hAlign && stylesClasses(hAlign, 'textAlign--', classes),
				className
			)}
			{...other}
		>
			{children}
		</Component>
	)
})

Box.displayName = 'Box'

export default Box

Box.defaultProps = {
	component: 'div'
}

/**
 * Functions to validate if props are within the allowed
 * range of types and values, as so the mediaqueries are too.
 */
const isSpacing = (props, propName, componentName) => {
	const value = props[propName]
	const spacingPairs = new Map(Object.entries(SPACING_VALUES))
	const spacingValues = [...spacingPairs.keys()]

	if (typeof value === 'object') {
		let error = null
		const keys = Object.keys(value)
		keys.every((key) => {
			if (!mediaQueries[key]) {
				error = new Error(
					`${propName} object in ${componentName} has an invalid key. Accepted keys: ${Object.keys(
						mediaQueries
					).join(', ')}`
				)
				return false
			}
			if (!(value[key] in spacingValues)) {
				error = new Error(
					`${propName}.${key} in ${componentName} is not a valid value. Valid values: ${spacingValues.join(
						', '
					)}`
				)
				return false
			}
			return true
		})
		if (error) {
			return error
		}
		return null
	} else if (typeof value === 'undefined') {
		return null
	} else if (typeof value === 'number') {
		return value in spacingValues // TO IMPROVE -> take keys from SPACING_VALUES as numbers for the prop comparison. Object and Map return keys as strings.
			? null
			: new Error(
				`${propName} in ${componentName} is not a valid value. Valid values: ${spacingValues.join(', ')}`
			)
	}

	return new Error(`${propName} in ${componentName} is not a valid value. Valid values: ${spacingValues.join(', ')}`)
}

const isNumber = (props, propName, componentName) => {
	const value = props[propName]

	if (typeof value === 'object') {
		let error = null
		const keys = Object.keys(value)
		keys.every((key) => {
			if (!mediaQueries[key]) {
				error = new Error(
					`${propName} object in ${componentName} has an invalid key. Accepted keys: ${Object.keys(
						mediaQueries
					).join(', ')}`
				)
				return false
			}
			if (typeof value[key] === 'number') {
				return true
			}
			error = new Error(`${propName} in ${componentName} is not a valid value. Valid values: number`)
			return false
		})
		if (error) {
			return error
		}
		return null
	} else if (typeof value === 'number' || typeof value === 'undefined') {
		return null
	}
	return new Error(`${propName} in ${componentName} is not a valid value. Valid values: number or object`)
}

const isString = (props, propName, componentName) => {
	const value = props[propName]

	if (typeof value === 'object') {
		let error = null
		const keys = Object.keys(value)
		keys.every((key) => {
			if (!mediaQueries[key]) {
				error = new Error(
					`${propName} object in ${componentName} has an invalid key. Accepted keys: ${Object.keys(
						mediaQueries
					).join(', ')}`
				)
				return false
			}
			if (typeof value[key] === 'string') {
				return true
			}
			error = new Error(`${propName} in ${componentName} is not a valid value. Valid values: string`)
			return false
		})
		if (error) {
			return error
		}
		return null
	} else if (typeof value === 'string' || typeof value === 'undefined') {
		return null
	}
	return new Error(`${propName} in ${componentName} is not a valid value. Valid values: string or object`)
}

const isNumberString = (props, propName, componentName) => {
	const value = props[propName]

	if (typeof value === 'object') {
		let error = null
		const keys = Object.keys(value)
		keys.every((key) => {
			if (!mediaQueries[key]) {
				error = new Error(
					`${propName} object in ${componentName} has an invalid key. Accepted keys: ${Object.keys(
						mediaQueries
					).join(', ')}`
				)
				return false
			}
			if (typeof value[key] === 'string' || typeof value[key] === 'number') {
				return true
			}
			error = new Error(`${propName} in ${componentName} is not a valid value. Valid values: number, string`)
			return false
		})
		if (error) {
			return error
		}
		return null
	} else if (typeof value === 'string' || typeof value === 'number' || typeof value === 'undefined') {
		return null
	}

	return new Error(`${propName} in ${componentName} is not a valid value. Valid values: number, string or object`)
}

const isBoolean = (props, propName, componentName) => {
	const value = props[propName]

	if (typeof value === 'object') {
		let error = null
		const keys = Object.keys(value)
		keys.every((key) => {
			if (!mediaQueries[key]) {
				error = new Error(
					`${propName} object in ${componentName} has an invalid key. Accepted keys: ${Object.keys(
						mediaQueries
					).join(', ')}`
				)
				return false
			}
			if (typeof value[key] === 'boolean') {
				return true
			}
			error = new Error(`${propName} in ${componentName} is not a valid value. Valid values: boolean`)
			return false
		})
		if (error) {
			return error
		}
		return null
	} else if (typeof value === 'boolean' || typeof value === 'undefined') {
		return null
	}
	return new Error(`${propName} in ${componentName} is not a valid value. Valid values: boolean or object`)
}

const isOverflow = (props, propName, componentName) => {
	const value = props[propName]
	const validValues = ['visible', 'hidden', 'scroll', 'auto']

	if (typeof value === 'object') {
		let error = null
		const keys = Object.keys(value)
		keys.every((key) => {
			if (!mediaQueries[key]) {
				error = new Error(
					`${propName} object in ${componentName} has an invalid key. Accepted keys: ${Object.keys(
						mediaQueries
					).join(', ')}`
				)
				return false
			}
			if (typeof value[key] === 'string' && validValues.includes(value)) {
				return true
			}
			error = new Error(
				`${propName} in ${componentName} is not a valid value. Valid values (string): ${validValues.join(
					', '
				)}`
			)
			return false
		})
		if (error) {
			return error
		}
		return null
	} else if (typeof value === 'string') {
		return validValues.includes(value)
			? null
			: new Error(
				`${propName} in ${componentName} is not a valid value. Valid values (string): ${validValues.join(
					', '
				)}`
			)
	} else if (typeof value === 'undefined') {
		return null
	}
	return new Error(
		`${propName} in ${componentName} is not a valid value. Valid values (string): ${validValues.join(', ')}`
	)
}

const isPosition = (props, propName, componentName) => {
	const value = props[propName]
	const validValues = ['static', 'relative', 'absolute', 'fixed', 'sticky']

	if (typeof value === 'object') {
		let error = null
		const keys = Object.keys(value)
		keys.every((key) => {
			if (!mediaQueries[key]) {
				error = new Error(
					`${propName} object in ${componentName} has an invalid key. Accepted keys: ${Object.keys(
						mediaQueries
					).join(', ')}`
				)
				return false
			}
			if (typeof value[key] !== 'string' || !validValues.includes(value)) {
				error = new Error(
					`${propName} in ${componentName} is not a valid value. Valid values (string): ${validValues.join(
						', '
					)}`
				)
				return false
			}
			return true
		})
		if (error) {
			return error
		}
	} else if (typeof value === 'string') {
		return validValues.includes(value)
			? null
			: new Error(
				`${propName} in ${componentName} is not a valid value. Valid values (string): ${validValues.join(
					', '
				)}`
			)
	} else if (typeof value === 'undefined') {
		return null
	}
	return new Error(
		`${propName} in ${componentName} is not a valid value. Valid values (string): ${validValues.join(', ')}`
	)
}

const isColor = (props, propName, componentName) => {
	const value = props[propName]
	const colorPairs = new Map(Object.entries(COLORS))

	if (typeof value === 'object') {
		let error = null
		const keys = Object.keys(value)
		keys.every((key) => {
			if (!mediaQueries[key]) {
				error = new Error(
					`${propName} object in ${componentName} has an invalid key. Accepted keys: ${Object.keys(
						mediaQueries
					).join(', ')}`
				)
				return false
			}
			if (typeof value[key] === 'string' && colorPairs.has(value[key])) {
				return true
			}
			error = new Error(
				`${propName} in ${componentName} is not a valid value. Valid values (string): ${Object.keys(
					COLORS
				).join(', ')}`
			)
			return false
		})
		if (error) {
			return error
		}
		return null
	} else if (typeof value === 'string') {
		return colorPairs.has(value)
			? null
			: new Error(
				`${propName} in ${componentName} is not a valid value. Valid values (string): ${Object.keys(
					COLORS
				).join(', ')}`
			)
	} else if (typeof value === 'undefined') {
		return null
	}

	return new Error(
		`${propName} in ${componentName} is not a valid value. Valid values (string): ${Object.keys(COLORS).join(', ')}`
	)
}

const isHorizAlign = (props, propName, componentName) => {
	const value = props[propName]
	const validValues = ['left', 'right', 'center', 'justify']

	if (typeof value === 'object') {
		let error = null
		const keys = Object.keys(value)
		keys.every((key) => {
			if (!mediaQueries[key]) {
				error = new Error(
					`${propName} object in ${componentName} has an invalid key. Accepted keys: ${Object.keys(
						mediaQueries
					).join(', ')}`
				)
				return false
			}

			if (typeof value[key] === 'string' && validValues.includes(value[key])) {
				return true
			}
			error = new Error(
				`${propName} in ${componentName} is not a valid value. Valid values (string): ${validValues.join(
					', '
				)}`
			)
			return false
		})
		if (error) {
			return error
		}
		return null
	} else if (typeof value === 'string') {
		return validValues.includes(value)
			? null
			: new Error(
				`${propName} in ${componentName} is not a valid value. Valid values (string): ${validValues.join(
					', '
				)}`
			)
	} else if (typeof value === 'undefined') {
		return null
	}
	return new Error(
		`${propName} in ${componentName} is not a valid value. Valid values (string): ${validValues.join(', ')}`
	)
}

const isVertAlign = (props, propName, componentName) => {
	const value = props[propName]
	const validValues = ['top', 'middle', 'bottom']

	if (typeof value === 'object') {
		let error = null
		const keys = Object.keys(value)
		keys.every((key) => {
			if (!mediaQueries[key]) {
				error = new Error(
					`${propName} object in ${componentName} has an invalid key. Accepted keys: ${Object.keys(
						mediaQueries
					).join(', ')}`
				)
				return false
			}
			if (typeof value[key] === 'string' && validValues.includes(value[key])) {
				return true
			}
			error = new Error(
				`${propName} in ${componentName} is not a valid value. Valid values (string): ${validValues.join(
					', '
				)}`
			)
			return false
		})
		if (error) {
			return error
		}
		return null
	} else if (typeof value === 'string') {
		return validValues.includes(value)
			? null
			: new Error(
				`${propName} in ${componentName} is not a valid value. Valid values (string): ${validValues.join(
					', '
				)}`
			)
	} else if (typeof value === 'undefined') {
		return null
	}
	return new Error(
		`${propName} in ${componentName} is not a valid value. Valid values (string): ${validValues.join(', ')}`
	)
}

Box.defaultProps = {
	component: 'div'
}

Box.propTypes = {
	/**
	 * The content of the box.
	 */
	children: PropTypes.node,
	/**
	 * The component used for the root node.
	 * Either a string to use a HTML element or a component.
	 * * @default 'div'
	 */
	component: PropTypes.elementType,

	/**
	 * The pading of the box,
	 * one of the allowed spacing values
	 */
	padding: isSpacing,
	paddingX: isSpacing,
	paddingY: isSpacing,
	paddingTop: isSpacing,
	paddingRight: isSpacing,
	paddingBottom: isSpacing,
	paddingLeft: isSpacing,
	/**
	 * The margin of the box,
	 * one of the allowed spacing values
	 */
	margin: isSpacing,
	marginX: isSpacing,
	marginY: isSpacing,
	marginTop: isSpacing,
	marginRight: isSpacing,
	marginBottom: isSpacing,
	marginLeft: isSpacing,

	/**
	 * The borders of the box, numeric (px) or string {"em","rem","vw"...}
	 */
	border: isNumberString,
	borderTop: isNumberString,
	borderRight: isNumberString,
	borderBottom: isNumberString,
	borderLeft: isNumberString,
	borderColor: isColor,
	borderCircle: isBoolean,
	borderRadius: isNumber,

	/**
	 * The background color of the box,
	 * one of the preset colors from the UI.
	 */
	bgColor: isColor,

	/**
	 * Oveflow property of the box.
	 */
	overflow: isOverflow,
	overflowX: isOverflow,
	overflowY: isOverflow,

	/**
	 * Position of the box,
	 * relative to its parent.
	 */
	position: isPosition,
	positionedTop: isNumberString,
	positionedRight: isNumberString,
	positionedBottom: isNumberString,
	positionedLeft: isNumberString,

	/**
	 * Box-Shadow of the box,
	 * being horizontal offset, vertical offset
	 * and color mandatory to be setted.
	 */
	shadowHoriz: isNumber,
	shadowVert: isNumber,
	shadowColor: isString,
	shadowBlur: isNumber,
	shadowSpread: isNumber,

	/**
	 * Text and vertical alignment inside the box,
	 */
	vAlign: isVertAlign,
	hAlign: isHorizAlign,

	/**
	 * Width and Height of the Box' div
	 */
	width: isNumberString,
	height: isNumberString
}
